import {
  Biohazard,
  GlassWater,
  Snowflake,
  ThermometerSun,
  WindIcon,
} from "lucide-react";
import { useMemo } from "react";
import { ProtectionRiskType } from "@bienesis/common";
import { useTranslation } from "react-i18next";

import { useActiveProtections } from "@/api/protection/queries";
import { PROTECTION_KEY_TO_RISK_TYPE } from "@/api/protection/types";
import ProtectionListSkeleton from "./protection-list.skeleton";
import SectionTitle from "@/components/section-title.component";

import { BubbleIcon } from "../components/icons/BubbleIcon";
import { MeteorIcon } from "../components/icons/MeteorIcon";
import { SelectableRow } from "../components/SelectableRow";
import { ProtectionKey } from "./enums/protection-key.enum";
import { SheetsEnum } from "./enums/sheets.enum";
import { SheetsType } from "./types/sheets.type";
import tw from "twin.macro";
import OvalBadge from "@/components/oval-badge.component";

type ProtectionCard = {
  protectionKey: ProtectionKey;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  available: boolean;
};

const NATURAL_RISKS: ProtectionCard[] = [
  {
    protectionKey: ProtectionKey.HAIL,
    icon: <MeteorIcon size={30} strokeWidth={25} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: true,
  },
  {
    protectionKey: ProtectionKey.WIND,
    icon: <WindIcon css={tw`w-5 h-5`} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: false,
  },
];

const AGRONOMIC_RISKS: ProtectionCard[] = [
  {
    protectionKey: ProtectionKey.HEAT,
    icon: <ThermometerSun css={tw`w-5 h-5`} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: true,
  },
  {
    protectionKey: ProtectionKey.DROUGHT,
    icon: <GlassWater css={tw`w-5 h-5`} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: false,
  },
  {
    protectionKey: ProtectionKey.FROST,
    icon: <Snowflake css={tw`w-5 h-5`} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: false,
  },
  {
    protectionKey: ProtectionKey.CONTAMINATING_RAIN,
    icon: <Biohazard css={tw`w-5 h-5`} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: false,
  },
  {
    protectionKey: ProtectionKey.LEACHING_RAIN,
    icon: <BubbleIcon size={20} />,
    iconBg: "#FFFFFF",
    iconColor: "#166534",
    available: false,
  },
];

function ProtectionRow({
  card,
  armed,
  pushSheet,
  isLast,
  available,
}: {
  card: ProtectionCard;
  armed: boolean;
  pushSheet: (view: SheetsType) => void;
  isLast: boolean;
  available: boolean;
}) {
  const { t: translator } = useTranslation();

  return (
    <SelectableRow
      icon={card.icon}
      iconBg={card.iconBg}
      iconColor={card.iconColor}
      label={translator(`protection.${card.protectionKey}`)}
      active={armed}
      available={available}
      isLast={isLast}
      onClick={() =>
        pushSheet({
          id: SheetsEnum.PROTECTION_DETAIL,
          canGoBack: true,
          protectionKey: card.protectionKey,
        })
      }
      trailing={
        !available ? (
          <OvalBadge
            textToDisplay={translator("protection.comingSoon")}
            primary={false}
            disabled={true}
          />
        ) : armed ? (
          <OvalBadge
            textToDisplay={translator("protection.armed")}
            primary={true}
          />
        ) : (
          <OvalBadge
            textToDisplay={translator("protection.notArmed")}
            primary={false}
          />
        )
      }
    />
  );
}

function SectionBlock({
  titleKey,
  cards,
  armedRiskTypes,
  pushSheet,
}: {
  titleKey: string;
  cards: ProtectionCard[];
  armedRiskTypes: Set<ProtectionRiskType>;
  pushSheet: (view: SheetsType) => void;
}) {
  return (
    <div css={tw`flex flex-col`}>
      <SectionTitle>{titleKey}</SectionTitle>
      <div css={tw`rounded-xl border overflow-hidden`}>
        {cards.map((card, i) => (
          <ProtectionRow
            key={card.protectionKey}
            card={card}
            armed={armedRiskTypes.has(
              PROTECTION_KEY_TO_RISK_TYPE[card.protectionKey]
            )}
            available={card.available}
            pushSheet={pushSheet}
            isLast={i === cards.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProtectionListSheetBody({
  areaId,
  pushSheet,
}: {
  areaId: string;
  pushSheet: (view: SheetsType) => void;
}) {
  const { data, isLoading } = useActiveProtections(areaId);

  const { t: translator } = useTranslation();

  const armedRiskTypes = useMemo(
    () =>
      new Set(
        (data?.protections ?? [])
          .filter((p) => p.activatedAt)
          .map((p) => p.riskType)
      ),
    [data]
  );

  if (isLoading) {
    return (
      <ProtectionListSkeleton
        sections={[NATURAL_RISKS.length, AGRONOMIC_RISKS.length]}
      />
    );
  }

  return (
    <div css={tw`flex flex-col gap-4 px-4 pb-6`}>
      <SectionBlock
        titleKey={translator("protection.naturalRisks")}
        cards={NATURAL_RISKS}
        armedRiskTypes={armedRiskTypes}
        pushSheet={pushSheet}
      />
      <SectionBlock
        titleKey={translator("protection.agronomicRisks")}
        cards={AGRONOMIC_RISKS}
        armedRiskTypes={armedRiskTypes}
        pushSheet={pushSheet}
      />
    </div>
  );
}
